import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    alpha,
    useTheme,
    LinearProgress
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import StudentLayout from 'components/layout/StudentLayout';
import CountdownTimer from 'components/quiz/CountdownTimer';
import AnswerOptionButton from 'components/quiz/AnswerOptionButton';
import {
    getQuiz,
    startQuizAttempt,
    submitAnswer,
    completeQuizAttempt,
    getMyAttemptWithResponses,
} from 'services/quizService';
import { QuizWithQuestions, QuizAttempt } from 'types/quiz';

function QuizPlayPageV2() {
    const theme = useTheme();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const quizId = Number(id);

    const [quiz, setQuiz] = useState<QuizWithQuestions | null>(null);
    const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [pointsEarned, setPointsEarned] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showStartDialog, setShowStartDialog] = useState(false);
    const [questionTimeRemaining, setQuestionTimeRemaining] = useState(0);
    const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

    const loadQuiz = useCallback(async () => {
        try {
            setLoading(true);

            const quizData = await getQuiz(quizId);
            setQuiz(quizData);

            const existingAttempt = quizData.attempt;

            if (existingAttempt && existingAttempt.completedAt) {
                navigate(`/student/v2/quizzes/${quizId}/results`);
                return;
            }

            if (existingAttempt && !existingAttempt.completedAt) {
                const attemptWithResponses = await getMyAttemptWithResponses(quizId);
                if (attemptWithResponses) {
                    setAttempt(existingAttempt);

                    const answeredQuestionIds = new Set(
                        attemptWithResponses.responses.map(r => r.questionId)
                    );

                    let resumeIndex = 0;
                    for (let i = 0; i < quizData.questions.length; i++) {
                        if (!answeredQuestionIds.has(quizData.questions[i].id)) {
                            resumeIndex = i;
                            break;
                        }
                    }

                    if (answeredQuestionIds.size === quizData.questions.length) {
                        await completeQuizAttempt(quizId, existingAttempt.id);
                        navigate(`/student/v2/quizzes/${quizId}/results`);
                        return;
                    }

                    setCurrentQuestionIndex(resumeIndex);

                    const resumeQuestion = quizData.questions[resumeIndex];
                    if (resumeQuestion?.timeLimitSeconds) {
                        setQuestionTimeRemaining(resumeQuestion.timeLimitSeconds);
                    }
                }
                setLoading(false);
            } else {
                setShowStartDialog(true);
                setLoading(false);
            }
        } catch (err: unknown) {
            console.error('[loadQuiz] Error loading quiz:', err);
            const error = err as { response?: { data?: { error?: string } } };
            setError(error.response?.data?.error || 'Failed to load quiz');
            setLoading(false);
        }
    }, [quizId, navigate]);

    useEffect(() => {
        loadQuiz();
    }, [loadQuiz]);

    useEffect(() => {
        if (attempt && !showFeedback && quiz) {
            const currentQuestion = quiz.questions[currentQuestionIndex];
            if (currentQuestion?.timeLimitSeconds) {
                setQuestionTimeRemaining(currentQuestion.timeLimitSeconds);
            }
            setQuestionStartTime(Date.now());
        }
    }, [currentQuestionIndex, attempt, showFeedback, quiz]);

    const handleStartQuiz = async () => {
        try {
            setLoading(true);
            const newAttempt = await startQuizAttempt(quizId);
            setAttempt(newAttempt);
            setShowStartDialog(false);

            // Initialize timer for first question
            if (quiz && quiz.questions[0]?.timeLimitSeconds) {
                setQuestionTimeRemaining(quiz.questions[0].timeLimitSeconds);
            }
            setQuestionStartTime(Date.now());

            setLoading(false);
        } catch (err: unknown) {
            console.error('Error starting quiz:', err);
            const error = err as { response?: { data?: { error?: string; message?: string }; status?: number } };

            if (error.response?.status === 400 && error.response?.data?.error?.includes('Maximum number of attempts')) {
                setError('You have already completed this quiz and cannot take it again.');
                setShowStartDialog(false);
            } else {
                setError(error.response?.data?.error || error.response?.data?.message || 'Failed to start quiz');
            }
            setLoading(false);
        }
    };

    const handleSelectOption = (optionId: number) => {
        if (showFeedback) return;
        setSelectedOptionId(optionId);
    };

    const triggerConfetti = () => {
        const duration = 2000;
        const animationEnd = Date.now() + duration;
        const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

        const randomInRange = (min: number, max: number) => {
            return Math.random() * (max - min) + min;
        };

        const interval = setInterval(() => {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
                return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);

            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
            });
            confetti({
                ...defaults,
                particleCount,
                origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
            });
        }, 250);
    };

    const triggerCompletionCelebration = () => {
        const duration = 3000;
        const end = Date.now() + duration;

        const frame = () => {
            confetti({
                particleCount: 7,
                angle: 60,
                spread: 55,
                origin: { x: 0, y: 0.8 },
                colors: ['#FFD700', '#FFA500', '#FF8C00', '#4CAF50'],
            });
            confetti({
                particleCount: 7,
                angle: 120,
                spread: 55,
                origin: { x: 1, y: 0.8 },
                colors: ['#FFD700', '#FFA500', '#FF8C00', '#4CAF50'],
            });

            if (Date.now() < end) {
                requestAnimationFrame(frame);
            }
        };

        frame();
    };

    const handleSubmitAnswer = async () => {
        if (!selectedOptionId || !attempt || !quiz) return;

        try {
            setSubmitting(true);
            const currentQuestion = quiz.questions[currentQuestionIndex];
            const responseTimeSeconds = Math.floor((Date.now() - questionStartTime) / 1000);

            const response = await submitAnswer(
                quizId,
                attempt.id,
                currentQuestion.id,
                selectedOptionId,
                responseTimeSeconds
            );

            setIsCorrect(response.isCorrect);
            setPointsEarned(response.pointsEarned || 0);
            setShowFeedback(true);

            if (response.isCorrect) {
                triggerConfetti();
            }

            setTimeout(() => {
                if (currentQuestionIndex < quiz.questions.length - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                    setSelectedOptionId(null);
                    setShowFeedback(false);
                    setSubmitting(false);
                    setQuestionStartTime(Date.now());
                } else {
                    triggerCompletionCelebration();
                    setTimeout(() => {
                        handleCompleteQuiz();
                    }, 1500);
                }
            }, 2000);
        } catch (err: unknown) {
            console.error('Error submitting answer:', err);
            const error = err as { response?: { data?: { error?: string } } };
            setError(error.response?.data?.error || 'Failed to submit answer');
            setSubmitting(false);
        }
    };

    const handleCompleteQuiz = async () => {
        if (!attempt) return;

        try {
            await completeQuizAttempt(quizId, attempt.id);
            navigate(`/student/v2/quizzes/${quizId}/results`);
        } catch (err: unknown) {
            console.error('Error completing quiz:', err);
            const error = err as { response?: { data?: { error?: string } } };
            setError(error.response?.data?.error || 'Failed to complete quiz');
        }
    };

    const handleTimeExpired = async () => {
        if (!attempt || !quiz) return;

        if (selectedOptionId && !showFeedback) {
            await handleSubmitAnswer();
        } else {
            if (currentQuestionIndex < quiz.questions.length - 1) {
                setCurrentQuestionIndex(currentQuestionIndex + 1);
                setSelectedOptionId(null);
                setQuestionStartTime(Date.now());
            } else {
                await handleCompleteQuiz();
            }
        }
    };

    if (loading) {
        return (
            <StudentLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </StudentLayout>
        );
    }

    if (error) {
        return (
            <StudentLayout>
                <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
                        {error}
                    </Alert>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/student/v2/quizzes')}
                        sx={{ borderRadius: 2 }}
                    >
                        Back to Quizzes
                    </Button>
                </Box>
            </StudentLayout>
        );
    }

    if (!quiz) {
        return (
            <StudentLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <Alert severity="info" sx={{ borderRadius: 3 }}>
                        Loading quiz...
                    </Alert>
                </Box>
            </StudentLayout>
        );
    }

    const currentQuestion = quiz.questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

    return (
        <StudentLayout>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                {/* Start Quiz Dialog */}
                <Dialog open={showStartDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle>
                        <Typography variant="h5" fontWeight="700">
                            Start Quiz: {quiz.title}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" paragraph sx={{ mt: 1 }}>
                            Once you start, each question will have its own timer. Answer quickly to earn bonus points!
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                • {quiz.questions.length} questions<br />
                                • Each question has a time limit<br />
                                • Faster correct answers earn more points<br />
                                • You cannot pause or retake this quiz<br />
                                • Make sure you have a stable internet connection
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 0 }}>
                        <Button
                            onClick={() => navigate('/student/v2/quizzes')}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleStartQuiz}
                            variant="contained"
                            size="large"
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 4 }}
                        >
                            Start Quiz
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Quiz Header */}
                <Box
                    sx={{
                        mb: 4,
                        p: 3,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                    }}
                >
                    <Typography variant="h4" fontWeight="700" gutterBottom>
                        {quiz.title}
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        {/* Progress Bar */}
                        <Grid item xs={12} md={questionTimeRemaining > 0 ? 6 : 12}>
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" fontWeight="600" color="text.secondary">
                                        Question {currentQuestionIndex + 1} of {quiz.questions.length}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="700" color="primary">
                                        {Math.round(progress)}% Complete
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                                        }
                                    }}
                                />
                            </Box>
                        </Grid>

                        {/* Timer */}
                        {questionTimeRemaining > 0 && (
                            <Grid item xs={12} md={6}>
                                <CountdownTimer
                                    key={currentQuestionIndex}
                                    totalSeconds={questionTimeRemaining}
                                    onExpire={handleTimeExpired}
                                    isPaused={showFeedback}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>

                {/* Question */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 4,
                        mb: 3,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.paper'
                    }}
                >
                    <Typography variant="h5" fontWeight="600" sx={{ lineHeight: 1.6 }}>
                        {currentQuestion.questionText}
                    </Typography>
                </Paper>

                {/* Answer Options */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {currentQuestion.options?.map((option, index) => (
                        <Grid item xs={12} sm={6} key={option.id}>
                            <AnswerOptionButton
                                option={option}
                                optionIndex={index}
                                selected={selectedOptionId === option.id}
                                onClick={() => handleSelectOption(option.id)}
                                disabled={showFeedback || submitting}
                                showFeedback={showFeedback}
                                isCorrect={isCorrect}
                            />
                        </Grid>
                    ))}
                </Grid>

                {/* Submit Button */}
                {!showFeedback && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleSubmitAnswer}
                            disabled={!selectedOptionId || submitting}
                            sx={{
                                minWidth: 200,
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1rem'
                            }}
                        >
                            {submitting ? 'Submitting...' : 'Submit Answer'}
                        </Button>
                    </Box>
                )}

                {/* Feedback */}
                <AnimatePresence>
                    {showFeedback && (
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{
                                scale: 1,
                                opacity: 1,
                                x: isCorrect ? 0 : [-10, 10, -10, 10, -5, 5, 0],
                            }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: isCorrect ? 0.3 : 0.5 }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    bgcolor: isCorrect
                                        ? alpha(theme.palette.success.main, 0.1)
                                        : alpha(theme.palette.error.main, 0.1),
                                    border: `2px solid ${isCorrect ? theme.palette.success.main : theme.palette.error.main}`,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    fontWeight="700"
                                    color={isCorrect ? 'success.dark' : 'error.dark'}
                                    gutterBottom
                                >
                                    {isCorrect ? '🎉 Correct!' : '😔 Incorrect'}
                                </Typography>
                                {isCorrect && (
                                    <motion.div
                                        initial={{ scale: 0.5, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                                    >
                                        <Typography
                                            variant="h4"
                                            fontWeight="700"
                                            color="success.dark"
                                            sx={{ my: 1 }}
                                        >
                                            +{pointsEarned} points!
                                        </Typography>
                                    </motion.div>
                                )}
                                {!isCorrect && (
                                    <Typography variant="h5" fontWeight="700" color="error.dark" sx={{ my: 1 }}>
                                        0 points
                                    </Typography>
                                )}
                                <Typography variant="body1" color={isCorrect ? 'success.dark' : 'error.dark'}>
                                    {currentQuestionIndex < quiz.questions.length - 1
                                        ? 'Moving to next question...'
                                        : 'Completing quiz...'}
                                </Typography>
                            </Paper>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>
        </StudentLayout>
    );
}

export default QuizPlayPageV2;
